<template>
    <div>
        <a-select class="custom-comp" allowClear
            :placeholder="placeholderStr"
            v-model:value="currentValue"
            mode="multiple"
            @change="changeValue"
            @search="debounceSearchByBackEnd"
            @popupScroll="onPopupScroll"
            @blur="blurSelect"
            :filterOption="searchByFrontEnd"
            show-search
            :disabled="disabled">
            <!-- 切换option时，value可能一样，所以添加javaField + value来进行唯一判定 -->
            <template v-for="item in options" :key="column.javaField + item.value">
                <a-select-option
                    v-bind="item"
                    :disabled="calcDisableOption(item)">
                    <div :title="item.title" class="option-container global-ellipsis">
                        <img v-if="item.showSubIcon" class="sub-icon" :src="sub_icon" />
                        <span v-if="item.icon" class="fusion-iconfont" :class="'icon-' + item.icon"></span>
                        <span class="option-title global-ellipsis">{{ item.title }}</span>
                    </div>
                </a-select-option>
            </template>
        </a-select>
    </div>
</template>

<script lang="ts" name="filterSelect" setup>
import { computed, ref, watch } from 'vue';
import { debounce } from 'lodash-es';
import { getDictItemsByCode } from '/@/utils/dict';
import { customQuery } from '../api';
import { desensitize } from "/@/utils/common/desensitizeUtils";
import { DESENSITIZE_FIELDS } from "/@/utils/constants";
import { useUserStore } from '/@/store/modules/user';
import sub_icon from '/@/assets/icons/sub_icon.png'

const props = defineProps({
    rule: String,
    column: Object,
    value: [Array, String],
    disabled: Boolean,
    needLabel: Boolean,
});

let currentSearchKeys = [];
let isSplitPageQuery = false;
let searchByBackEndButNotSplitPage = false;
let backendSearchKey = '';
let lastEmptySearchOptionsByBackEnd = {
    currentPage: 1,
    data: [],
    maxPage: null,
};
// 用于记录后台分页时，有初始选中项，下发给后台的value返回的选中配置项
let initialSelectedOptions = [];

const debounceSearchByBackEnd = debounce(searchByBackEnd, 400)

const calcDisableOption = (item) => {
    const { getTenantDisabledDictItems } = useUserStore()
    // 判断item是否在租户禁用的字典项中
    if (getTenantDisabledDictItems?.includes(item.id)) {
        return true;
    }

    return (
        props.column.optionMaxNum && 
        currentValue.value.length >= props.column.optionMaxNum
    ) && !currentValue.value.includes(item.value);
}

const emit = defineEmits(['update:value', 'change'])

const currentValue = ref([])

const options = ref([]);
let queryParamsModel = initQueryParamsModel();
const showCustomOptionLabel = ref(false);
let customOptionLabelArr = [];
let hasSelectedOptions = [];

const showSubType = ref('');

watch(() => props.value, (value: Array<String>) => {
    if (props.needLabel) {
        currentValue.value = valueRemoveLabel(value);
    } else {
        currentValue.value = value || [];
    }
}, {
    immediate: true,
})

watch(() => props.column, (value) => {
    initOptions();
})

const debounceInitOptions = debounce(initOptions, 400)

watch(() => props.column.requestParameterMapping, () => {
    // 防止依赖参数变化过快时，保证后面的请求数据不会被覆盖，不考虑有多种相同条件的场景
    debounceInitOptions(false);
})

initOptions();

function changeValue() {
    let submitValue = currentValue.value
    if (props.needLabel) {
        addHasSelectedOptions()
        submitValue = valueAddLabel(currentValue.value)
    }
    emit('update:value', submitValue); 
    emit('change', submitValue)
    // 因为blurSelect会更改options，所以在选中的时候要添加延迟
    setTimeout(() => {
        blurSelect()
    }, 0);
}

function addHasSelectedOptions() {
    const extraOptions = options.value.filter(optionItem => {
        return currentValue.value.includes(optionItem.value) && !hasSelectedOptions.some(selectedItem => selectedItem.value === optionItem.value);
    })

    hasSelectedOptions = hasSelectedOptions.concat(extraOptions);
}

function valueAddLabel(values) {
    const allOptions = options.value.concat(hasSelectedOptions);
    const selectedOptions = values.map(valueItem => {
        const valueObj = allOptions.find(optionItem => optionItem.value === valueItem);
        return {
            value: valueObj.value,
            label: valueObj.label,
        }
    });

    return selectedOptions;
}

function valueRemoveLabel(values) {
    if (!values?.length) {
        return [];
    }

    return values.map(item => item.value);
}

// 当失去焦点时，如果有搜索内容，会自动清除，需要还原，只适用于后端搜索
function blurSelect() {
    if (!isSplitPageQuery && !searchByBackEndButNotSplitPage) {
        return;
    }

    options.value = lastEmptySearchOptionsByBackEnd.data;
    queryParamsModel.params[backendSearchKey] = '';
    queryParamsModel.params.pageNo = lastEmptySearchOptionsByBackEnd.currentPage;
    queryParamsModel.maxPage = lastEmptySearchOptionsByBackEnd.maxPage;
}

async function initOptions(ignoreCancelToken = true) {
    isSplitPageQuery = props.column.isPage === '1';
    searchByBackEndButNotSplitPage = props.column.isFilterSearch === '1';
    options.value = [];
    queryParamsModel = initQueryParamsModel();
    // 有可能要支持根据多个属性进行搜索，默认需要包含label属性
    currentSearchKeys = ['label'];
    const { dictType, dictTypeFlag, interfaceUrl, interfaceMethod, returnParameterMapping, requestParameterMapping, dictContent } = props.column;
    const { labelKey, valueKey, searchKeys, partitionSymbol, showType } = (returnParameterMapping || {})
    backendSearchKey = returnParameterMapping?.backendSearchKey;
    if (Array.isArray(searchKeys) && searchKeys.length) {
        currentSearchKeys = currentSearchKeys.concat(searchKeys)
    }

    showSubType.value = showType || ''
    //用中横线判断是否自定义拼接字段-由后端制定规则
    if(labelKey?.indexOf('-') !== -1){
      showCustomOptionLabel.value = true;
      customOptionLabelArr = labelKey?.split('-');
    }

    if (dictTypeFlag == 'custom') {
        options.value = dictContent?.map(item => ({
            id: item.id,
            value: item.value,
            label: item.label,
            title: item.label,
        })) ?? []
        return
    }
    
    if (dictType) {
        const dictIsPublic = dictTypeFlag !== 'private';
        let dictArr = getDictItemsByCode(props.column.dictType, dictIsPublic);
        options.value = dictArr.map(item => ({
            id: item.id,
            value: item.value,
            label: item.label,
            title: item.label,
        }))
        return;
    }

    if (interfaceUrl && interfaceMethod) {
        queryParamsModel.url = interfaceUrl;
        queryParamsModel.method = interfaceMethod;
        queryParamsModel.labelKey = labelKey;
        queryParamsModel.valueKey = valueKey;
        queryParamsModel.partitionSymbol = partitionSymbol || '-';
        if (requestParameterMapping) {
            Object.assign(queryParamsModel.params, requestParameterMapping);
        }

        if (isSplitPageQuery) {
            queryParamsModel.params.pageNo = 1;
            if (Array.isArray(props.value)) {
                initialSelectedOptions = currentValue.value.slice();
            }
            
        }
        
        await queryOptions(ignoreCancelToken);

        hasSelectedOptions = options.value?.filter?.(item => initialSelectedOptions?.includes(item.value)).map?.(item => ({
            id: item.id,
            value: item.value,
            label: item.label,
            title: item.label,
        })) || [];
    }
    
}

async function queryOptions(ignoreCancelToken = true) {
    let res = []
    try {
        const selectedKey = 'hasSelected';
        delete queryParamsModel.params[selectedKey];
        // 只在后端分页，且没有请求到数据时，才会在请求中下发选中id项，是为了防止非首页的选中项的回显问题
        if (isSplitPageQuery && initialSelectedOptions?.length && !queryParamsModel.params[backendSearchKey]) {
            // 只在第一页查询时下发选中id，前台会保存选中项
            if (queryParamsModel.params.pageNo === 1) {
                queryParamsModel.params[selectedKey] = initialSelectedOptions.join(',');
            }
        }
        res = await customQuery({url: queryParamsModel.url, method: queryParamsModel.method, params: queryParamsModel.params, ignoreCancelToken}) || [];
        if (res.current) { 
            queryParamsModel.params.pageNo = res.current + 1;
            queryParamsModel.maxPage = res.pages;
        };
    } catch (error) {
        return;
    }
    let queryResArr = isSplitPageQuery ? res?.records : res;
    if (initialSelectedOptions.length && options.value.length) {
        queryResArr = queryResArr.filter(item => {
            // 初始选中项在后面的分页中有可能会再次查出，做个过滤
            return !initialSelectedOptions.includes(item?.[queryParamsModel.valueKey])
        })
    }
    const newOptions = queryResArr?.map?.(item => {
        const result = getCustomOptionLabelFormat(item)
        // 添加需要搜索项到dom属性上，可以用option进行前台搜索
        currentSearchKeys.forEach(searchKey => {
            item[searchKey] && (result[searchKey] = item[searchKey])
        })
        if (item.icon) {
            result.icon = item.icon;
        }
        // 处理子项图标显示逻辑，后端返回的showType字段值等于1时不显示子项图标，其他情况只要有这个字段就显示
        if (showSubType.value && item?.[showSubType.value] != '1') {
            result.showSubIcon = true;
        }
        return result;
    })
    newOptions?.length && (options.value = options.value.concat(newOptions));
    if (!queryParamsModel.params[backendSearchKey]) {
        lastEmptySearchOptionsByBackEnd.data = options.value.slice();
        lastEmptySearchOptionsByBackEnd.currentPage = res.current + 1;
        lastEmptySearchOptionsByBackEnd.maxPage = res.pages;
    }
}

function getCustomOptionLabelFormat(optionItem) {
  if(showCustomOptionLabel.value){
    let titleFormat = '';
    customOptionLabelArr?.forEach((key,idx) => {
      let optionItemLabel = optionItem[key];
      if(optionItemLabel && DESENSITIZE_FIELDS.includes(key)){
        optionItemLabel = getOptionDesensitize(key, optionItemLabel)
      }
      if (idx == 0 && optionItemLabel) {
        titleFormat = optionItemLabel
      } else if(optionItemLabel) {
        titleFormat += queryParamsModel.partitionSymbol + optionItemLabel;
      }
    })
    return {
      value: optionItem[queryParamsModel.valueKey],
      title: titleFormat,
      label: titleFormat,
    };
  } else {
    return {
      value: optionItem[queryParamsModel.valueKey],
      title: optionItem[queryParamsModel.labelKey],
      label: optionItem[queryParamsModel.labelKey],
    }
  }
}

function getOptionDesensitize(key, label ) {
  let optionItemLabel = label
  if (key === 'name') {
    optionItemLabel = desensitize?.desensitizeName(optionItemLabel)
  }
  if (key === 'phone') {
    optionItemLabel = desensitize?.desensitizePhone(optionItemLabel)
  }
  return optionItemLabel
}

function searchByBackEnd(input) {
    if (!isSplitPageQuery && !searchByBackEndButNotSplitPage) {
        return;
    }

    if (backendSearchKey) {
        queryParamsModel.params[backendSearchKey] = input;
    }

    if (isSplitPageQuery) {
        queryParamsModel.maxPage = null;
        queryParamsModel.params.pageNo = 1;
    }

    options.value = [];
    queryOptions(false);
}

function searchByFrontEnd(input, option) {
    if (isSplitPageQuery || searchByBackEndButNotSplitPage) {
        return true;
    }
    return currentSearchKeys.some(searchKey => {
        return option[searchKey]?.toLowerCase?.().includes(input.toLowerCase())
    })
}

function onPopupScroll(e) {
    if (!isSplitPageQuery || (queryParamsModel.maxPage && queryParamsModel.maxPage < queryParamsModel.params.pageNo)) {
        return;
    }
    const dropDownEle = e.target;
    const distanceToQuery = 30;
    if (dropDownEle.scrollTop + dropDownEle.offsetHeight >= (dropDownEle.scrollHeight - distanceToQuery)) {
        queryOptions(false)
    }
}

function initQueryParamsModel() {
    return {
        url: '',
        method: '',
        params: {},
        labelKey: '',
        valueKey: '',
        partitionSymbol: '',//拼接option的字符格式
        maxPage: null,
    }
}

const placeholderStr = computed(() => {
    const customPlaceholder = props.column?.otherCustom?.placeholder;
    return customPlaceholder || ('请选择' + (props.column.columnComment || props.column.varName))
})

</script>

<style lang="less" scoped>
@import url('../assets/icons/fusion-iconfont.css');
.custom-comp {
    width: 100%;
}

:deep(.ant-select-selector) {
    max-width: 100%;
    max-height: 220px;
    overflow: auto;
}

.option-container {
    display: flex;
    align-items: center;
    .fusion-iconfont {
        margin-right: 4px;
    }
    .option-title {
        max-width: 100%;
    }
}

.sub-icon {
    width: 14px;
    height: 14px;
    margin-right: 4px;
}

:deep(.ant-select-selector) {
  .sub-icon {
    display: none;
  }
}

</style>
